import { useEffect, useMemo, useState } from "react";
import { adminApi, type GraphPresetDescriptor, type GraphResult, type ExplainResult } from "@/lib/admin";
import { ViewerPicker } from "@/components/ViewerPicker";

const ADHOC_PLACEHOLDER = `-- Tylko SELECT, jedno stwierdzenie, bez średnika.
-- Whitelista: dbo.ViewerNode, ChatNode, SaleNode, POSTED, DONATED, RAIDED_WITH, GIFTED_SUB_TO, MENTIONED
-- Przykład:
SELECT TOP (20) v.KickUsername, SUM(e.AmountPln) AS total
FROM dbo.ViewerNode v, dbo.DONATED e, dbo.SaleNode s
WHERE MATCH(v-(e)->s)
GROUP BY v.KickUsername
ORDER BY total DESC`;

export function AdminGraph() {
  const [presets, setPresets] = useState<GraphPresetDescriptor[] | null>(null);
  const [activePreset, setActivePreset] = useState<string>("");
  const [presetArgs, setPresetArgs] = useState<Record<string, string>>({});
  const [result, setResult] = useState<GraphResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState<"preset" | "adhoc">("preset");
  const [adhocSql, setAdhocSql] = useState(ADHOC_PLACEHOLDER);
  const [adhocLimit, setAdhocLimit] = useState(100);

  useEffect(() => {
    adminApi.graphPresets()
      .then((p) => {
        setPresets(p);
        if (p.length > 0) setActivePreset(p[0].key);
      })
      .catch((e) => setErr((e as Error).message));
  }, []);

  const currentPreset = useMemo(
    () => presets?.find((p) => p.key === activePreset),
    [presets, activePreset]
  );

  function setArg(key: string, value: string) {
    setPresetArgs((a) => ({ ...a, [key]: value }));
  }

  async function runPreset() {
    if (!currentPreset) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await adminApi.graphRunPreset(currentPreset.key, presetArgs);
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  async function runAdHoc() {
    setLoading(true);
    setErr(null);
    try {
      const r = await adminApi.graphRunAdHoc(adhocSql, adhocLimit);
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Graf widzów</h1>
        <p className="text-sm text-carp-ink/70 mt-1">
          Zapytania po SQL Server Graph Tables (NODE/EDGE). Nodes: <code>ViewerNode</code>,{" "}
          <code>ChatNode</code>, <code>SaleNode</code>. Edges: <code>POSTED</code>, <code>DONATED</code>,{" "}
          <code>RAIDED_WITH</code>, <code>GIFTED_SUB_TO</code>, <code>MENTIONED</code>.
        </p>
      </header>

      <div className="flex gap-2">
        <button
          type="button"
          className={`btn-ghost ${mode === "preset" ? "!bg-carp-yellow" : ""}`}
          onClick={() => setMode("preset")}
        >
          Presety
        </button>
        <button
          type="button"
          className={`btn-ghost ${mode === "adhoc" ? "!bg-carp-yellow" : ""}`}
          onClick={() => setMode("adhoc")}
        >
          Custom SQL
        </button>
      </div>

      {mode === "preset" && (
        <section className="brut bg-carp-paper p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="space-y-1">
              <span className="label-brut">Zapytanie</span>
              <select
                className="input-brut w-full"
                value={activePreset}
                onChange={(e) => {
                  setActivePreset(e.target.value);
                  setPresetArgs({});
                  setResult(null);
                }}
              >
                {presets?.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {currentPreset && (
            <p className="text-sm text-carp-ink/70">{currentPreset.description}</p>
          )}

          {currentPreset && currentPreset.args.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {currentPreset.args.map((arg) => (
                <label key={arg} className="space-y-1 block">
                  <span className="label-brut">{argLabel(arg)}</span>
                  {arg === "viewerId" ? (
                    <ViewerPicker
                      value={presetArgs[arg] ?? ""}
                      onChange={(viewerId) => setArg(arg, viewerId)}
                    />
                  ) : (
                    <input
                      type={arg === "limit" ? "number" : "text"}
                      min={arg === "limit" ? 1 : undefined}
                      max={arg === "limit" ? 500 : undefined}
                      className="input-brut w-full"
                      value={presetArgs[arg] ?? ""}
                      onChange={(e) => setArg(arg, e.target.value)}
                      placeholder={argHint(arg)}
                    />
                  )}
                </label>
              ))}
            </div>
          )}

          <button
            type="button"
            className="btn-primary"
            onClick={runPreset}
            disabled={loading || !currentPreset}
          >
            {loading ? "Pytam SQL Servera…" : "Uruchom"}
          </button>
        </section>
      )}

      {mode === "adhoc" && (
        <section className="brut bg-carp-paper p-5 space-y-4">
          <label className="space-y-1 block">
            <span className="label-brut">Custom MATCH query (read-only)</span>
            <textarea
              className="input-brut w-full font-mono text-sm"
              rows={10}
              value={adhocSql}
              onChange={(e) => setAdhocSql(e.target.value)}
              spellCheck={false}
            />
          </label>
          <div className="flex items-end gap-3">
            <label className="space-y-1">
              <span className="label-brut">Limit</span>
              <input
                type="number"
                min={1}
                max={500}
                className="input-brut w-24"
                value={adhocLimit}
                onChange={(e) => setAdhocLimit(Number(e.target.value))}
              />
            </label>
            <button type="button" className="btn-primary" onClick={runAdHoc} disabled={loading}>
              {loading ? "Pytam SQL Servera…" : "Uruchom"}
            </button>
          </div>
          <p className="text-xs text-carp-ink/60">
            Whitelist: tylko <code>SELECT</code>, jedno stwierdzenie, bez średników, bez DML/DDL.
            Limit dokładany automatycznie (1-500).
          </p>
        </section>
      )}

      {err && (
        <div className="brut bg-carp-destructive text-white p-3 font-mono text-sm whitespace-pre-wrap">
          {err}
        </div>
      )}

      {result && <ResultTable result={result} presetLabel={currentPreset?.label} />}
    </div>
  );
}

function argHint(name: string): string {
  switch (name) {
    case "viewerId":
      return "Wpisz nick widza…";
    case "limit":
      return "10";
    default:
      return "";
  }
}

function argLabel(name: string): string {
  switch (name) {
    case "viewerId":
      return "Widz";
    case "limit":
      return "Limit wyników";
    default:
      return name;
  }
}

function ResultTable({ result, presetLabel }: { result: GraphResult; presetLabel?: string }) {
  // Detect a ViewerId column so we can offer per-row "Wyjaśnij" via LLM.
  // We accept a few naming variants — preset queries may pick different
  // aliases ("ViewerId", "viewerId", "Id" when it's the viewer table).
  const viewerIdCol = useMemo(
    () => result.columns.findIndex((c) => /^viewer ?id$/i.test(c)),
    [result.columns]
  );
  const usernameCol = useMemo(
    () => result.columns.findIndex((c) => /username/i.test(c)),
    [result.columns]
  );

  const [explainTarget, setExplainTarget] = useState<{
    viewerId: string;
    username: string;
  } | null>(null);

  if (result.rowCount === 0) {
    return (
      <div className="brut bg-carp-paper p-4 text-sm text-carp-ink/70">
        Brak wyników (0 wierszy).
      </div>
    );
  }

  const canExplain = viewerIdCol !== -1;

  return (
    <>
      <section className="brut bg-carp-paper p-4 overflow-auto">
        <div className="text-xs text-carp-ink/60 mb-2">
          {result.rowCount} {result.rowCount === 1 ? "wiersz" : "wierszy"}
        </div>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b-[2px] border-carp-ink">
              {result.columns.map((c) => (
                <th key={c} className="text-left px-2 py-1 font-display uppercase">
                  {c}
                </th>
              ))}
              {canExplain && <th className="px-2 py-1 font-display uppercase">LLM</th>}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, i) => (
              <tr key={i} className="border-b border-carp-ink/20 hover:bg-carp-yellow/10">
                {row.map((cell, j) => (
                  <td key={j} className="px-2 py-1 font-mono align-top">
                    {cell === null ? <span className="text-carp-ink/40">∅</span> : String(cell)}
                  </td>
                ))}
                {canExplain && (
                  <td className="px-2 py-1">
                    <button
                      type="button"
                      className="btn-ghost text-xs px-2 py-1"
                      onClick={() =>
                        setExplainTarget({
                          viewerId: String(row[viewerIdCol]),
                          username:
                            usernameCol !== -1 && row[usernameCol] != null
                              ? String(row[usernameCol])
                              : "?",
                        })
                      }
                    >
                      Wyjaśnij
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {explainTarget && (
        <ExplainModal
          target={explainTarget}
          presetLabel={presetLabel}
          onClose={() => setExplainTarget(null)}
        />
      )}
    </>
  );
}

function ExplainModal(props: {
  target: { viewerId: string; username: string };
  presetLabel?: string;
  onClose: () => void;
}) {
  const defaultQuestion = useMemo(() => {
    // Question seeded from the preset that surfaced this row — so "top
    // negative" gets a "why is this person negative" question by default,
    // "top spammers" gets a "what topics dominate" question, etc.
    const label = props.presetLabel ?? "";
    if (/negatyw/i.test(label)) return "Dlaczego ten widz jest negatywny? Cytuj fragmenty.";
    if (/spam/i.test(label)) return "Co dominuje w wiadomościach tego widza? Czy to spam?";
    if (/emot/i.test(label)) return "Jakich emotek najczęściej używa? Kontekst emocjonalny?";
    return "Streść zachowanie tego widza w 3-5 zdaniach.";
  }, [props.presetLabel]);

  const [question, setQuestion] = useState(defaultQuestion);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState<ExplainResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pulling, setPulling] = useState(false);
  const [pullMessage, setPullMessage] = useState<string | null>(null);

  // Backend tags "model not pulled yet" errors with this magic prefix —
  // surface a one-click pull button instead of a wall of Ollama JSON.
  const modelNotPulled = err?.includes("MODEL_NOT_PULLED:") ?? false;

  async function run() {
    setWorking(true);
    setErr(null);
    setResult(null);
    try {
      const r = await adminApi.graphExplain(props.target.viewerId, question);
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  async function pullModel() {
    setPulling(true);
    setPullMessage("Ściągam model… to potrwa 10–30 min na CPU (9.6GB).");
    setErr(null);
    try {
      const r = await adminApi.graphLlmPull();
      setPullMessage(r.message);
    } catch (e) {
      setPullMessage(null);
      setErr("Pull failed: " + (e as Error).message);
    } finally {
      setPulling(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="brut bg-carp-paper max-w-2xl w-full max-h-[90vh] overflow-auto p-5 space-y-4">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl font-bold uppercase">
              Wyjaśnij: {props.target.username}
            </h3>
            <p className="text-xs text-carp-ink/60">Viewer {props.target.viewerId}</p>
          </div>
          <button type="button" className="btn-ghost px-3 py-1" onClick={props.onClose}>
            ✕
          </button>
        </header>

        <label className="block space-y-1">
          <span className="label-brut">Pytanie do LLM (Qwen 2.5 1.5B, lokalny)</span>
          <textarea
            className="input-brut w-full text-sm"
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </label>

        <div className="flex gap-2 items-center flex-wrap">
          <button type="button" className="btn-primary" onClick={run} disabled={working || pulling}>
            {working ? "Pyta Gemmę…" : "Zapytaj"}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={pullModel}
            disabled={working || pulling}
            title="Wymusza pobranie modelu Ollama (≈9.6GB, 10-30 min na CPU)"
          >
            {pulling ? "Pobieram model…" : "Pobierz model"}
          </button>
        </div>

        {pullMessage && (
          <div className="brut bg-carp-yellow/40 p-3 text-sm whitespace-pre-wrap">
            {pullMessage}
          </div>
        )}

        {err && modelNotPulled && (
          <div className="brut bg-carp-destructive text-white p-3 text-sm space-y-2">
            <p>
              Model jest jeszcze nie pobrany na serwer Ollama. Kliknij{" "}
              <strong>„Pobierz model"</strong> powyżej (potrwa 10–30 min, ok. 9.6&nbsp;GB).
              Po sukcesie spróbuj <strong>„Zapytaj"</strong> ponownie.
            </p>
          </div>
        )}

        {err && !modelNotPulled && (
          <div className="brut bg-carp-destructive text-white p-3 text-sm whitespace-pre-wrap">
            {err}
          </div>
        )}

        {result && (
          <section className="brut bg-white p-4 space-y-2">
            <div className="text-xs text-carp-ink/60">
              Przeanalizowano {result.messagesAnalysed} wiadomości
            </div>
            <p className="whitespace-pre-wrap text-sm">{result.answer}</p>
          </section>
        )}
      </div>
    </div>
  );
}
